import React from 'react';
import styled, { keyframes } from 'styled-components';
import GlowBackground from './shared/GlowBackground';

const Section = styled.section`
  position: relative;
  overflow: hidden;
`;

const Hero = styled.div`
  position: relative;
  z-index: 1;
  max-width: 1200px;
  margin: 0 auto;
  padding: 80px 40px 100px;
  display: grid;
  grid-template-columns: 1fr 380px;
  gap: 80px;
  align-items: center;

  @media (max-width: 960px) {
    grid-template-columns: 1fr;
    gap: 48px;
    padding: 56px 20px 72px;
  }
`;

const HeroLeft = styled.div``;

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(12px); }
  to { opacity: 1; transform: translateY(0); }
`;

const ProTag = styled.div`
  font-family: ${p => p.theme.fontMono};
  font-size: 12px;
  color: ${p => p.theme.gold};
  margin-bottom: 24px;
  animation: ${fadeIn} 0.6s cubic-bezier(0.16, 1, 0.3, 1) both;
`;

const Headline = styled.h1`
  font-size: 56px;
  font-weight: 600;
  line-height: 1.08;
  letter-spacing: -0.035em;
  color: ${p => p.theme.text};
  margin-bottom: 24px;
  animation: ${fadeIn} 0.6s cubic-bezier(0.16, 1, 0.3, 1) 0.06s both;

  @media (max-width: 768px) {
    font-size: 40px;
  }

  @media (max-width: 480px) {
    font-size: 32px;
  }
`;

const Subtitle = styled.p`
  font-size: 17px;
  line-height: 1.65;
  color: ${p => p.theme.textSecondary};
  max-width: 440px;
  margin-bottom: 36px;
  animation: ${fadeIn} 0.6s cubic-bezier(0.16, 1, 0.3, 1) 0.12s both;

  @media (max-width: 768px) {
    font-size: 16px;
  }
`;

const CTARow = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;
  animation: ${fadeIn} 0.6s cubic-bezier(0.16, 1, 0.3, 1) 0.18s both;

  @media (max-width: 480px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
  }
`;

const PrimaryButton = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 48px;
  padding: 0 32px;
  background: ${p => p.theme.gold};
  color: #0a0c10;
  font-size: 15px;
  font-weight: 600;
  font-family: ${p => p.theme.fontFamily};
  border-radius: ${p => p.theme.radius};
  text-decoration: none;
  cursor: pointer;
  transition: opacity 0.2s ease, box-shadow 0.2s ease, transform 0.15s ease;

  &:hover {
    opacity: 0.92;
    box-shadow: 0 0 40px rgba(229, 188, 42, 0.2);
    transform: translateY(-1px);
  }

  &:active {
    opacity: 0.85;
    transform: translateY(0);
  }
`;

const SecondaryLink = styled.a`
  font-size: 14px;
  color: ${p => p.theme.textTertiary};
  text-decoration: none;
  transition: color 0.15s ease;

  &:hover {
    color: ${p => p.theme.textSecondary};
  }
`;

const HeroRight = styled.div`
  animation: ${fadeIn} 0.6s cubic-bezier(0.16, 1, 0.3, 1) 0.24s both;

  @media (max-width: 960px) {
    max-width: 420px;
  }
`;

const ConfigBlock = styled.div`
  background: var(--tz-wash, rgba(255, 255, 255, 0.03));
  border: 1px solid var(--tz-hairline, rgba(255, 255, 255, 0.06));
  border-radius: 12px;
  overflow: hidden;
`;

const ConfigBar = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  border-bottom: 1px solid var(--tz-wash, rgba(255, 255, 255, 0.04));
`;

const ConfigDot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--tz-hairline, rgba(255, 255, 255, 0.08));
`;

const ConfigTitle = styled.span`
  font-family: ${p => p.theme.fontMono};
  font-size: 12px;
  color: ${p => p.theme.textTertiary};
  margin-left: 6px;
`;

const ConfigCode = styled.pre`
  font-family: ${p => p.theme.fontMono};
  font-size: 13px;
  line-height: 1.7;
  color: ${p => p.theme.textSecondary};
  padding: 18px 20px;
  margin: 0;
  white-space: pre;
  overflow-x: auto;
  /* the pages layout ships Bootstrap, which skins bare <pre> light grey */
  background: transparent;
  border: 0;
`;

const K = styled.span`color: ${p => p.theme.accent};`;
const S = styled.span`color: #7ee787;`;
const D = styled.span`color: ${p => p.theme.textTertiary};`;

const CompatNote = styled.div`
  font-size: 12px;
  color: ${p => p.theme.textTertiary};
  margin-top: 14px;
  padding-left: 2px;
  animation: ${fadeIn} 0.6s cubic-bezier(0.16, 1, 0.3, 1) 0.3s both;
`;

export default function HeroSection() {
  return (
    <Section>
      <GlowBackground />

      <Hero>
        <HeroLeft>
          <ProTag>// Unlocked with TezLab Pro</ProTag>

          <Headline>
            Talk to your car<br />
            through any AI
          </Headline>

          <Subtitle>
            TezLab connects your Tesla or Rivian to AI assistants
            via MCP. Not just raw data — real reasoning
            over your charging, trips, and driving patterns.
          </Subtitle>

          <CTARow>
            <PrimaryButton href="/subscribe">
              Get TezLab Pro &rarr;
            </PrimaryButton>
            <SecondaryLink href="#tools">
              View all 20 tools &darr;
            </SecondaryLink>
          </CTARow>
        </HeroLeft>

        <HeroRight>
          <ConfigBlock>
            <ConfigBar>
              <ConfigDot /><ConfigDot /><ConfigDot />
              <ConfigTitle>mcp.json</ConfigTitle>
            </ConfigBar>
            <ConfigCode>
<D>{'{'}</D>{'\n'}
{'  '}<K>"mcpServers"</K><D>:</D> <D>{'{'}</D>{'\n'}
{'    '}<K>"tezlab"</K><D>:</D> <D>{'{'}</D>{'\n'}
{'      '}<K>"url"</K><D>:</D> <S>"https://mcp.tezlabapp.com"</S>{'\n'}
{'    '}<D>{'}'}</D>{'\n'}
{'  '}<D>{'}'}</D>{'\n'}
<D>{'}'}</D>
            </ConfigCode>
          </ConfigBlock>
          <CompatNote>
            Your AI, your choice. Works with any MCP client.
          </CompatNote>
        </HeroRight>
      </Hero>
    </Section>
  );
}
